import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SocialShare } from '@/components/SocialShare';

const AuthenticExperiences = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <div className="mb-4">
              <Badge>Leadership</Badge>
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Beyond the Escape Room: Why Authentic Experiences Create Stronger Teams
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>October 29, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>10 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <SocialShare 
                url={window.location.href}
                title="Beyond the Escape Room: Why Authentic Experiences Create Stronger Teams"
                description="When people are financially invested, they want a return. When they are emotionally invested, they want to contribute."
              />
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/team-building-bw.jpg" 
              alt="Team silhouettes standing together at sunset, representing authentic connection and collective purpose in leadership development"
              className="w-full h-[400px] md:h-[500px] object-cover"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
              "When people are financially invested, they want a return. When they are emotionally invested, they want to contribute."
              <footer className="text-sm text-muted-foreground mt-2 not-italic">
                — Simon Sinek, <em>Leaders Eat Last</em>, Portfolio/Penguin, 2014, p. 112
              </footer>
            </blockquote>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Problem with 'Team-Building' as We Know It</h2>

              <p>
                Every year, HR departments across the world plan team-building activities designed to "boost engagement" and "improve collaboration." Escape rooms, go-kart tracks, karaoke nights, pizza-making workshops — all meant to break routine and create bonds.
              </p>

              <p>
                But here's the paradox: most people leave those events feeling more exhausted than connected.
              </p>

              <p>
                According to Gallup's 2023 State of the Global Workplace Report, only 23% of employees worldwide feel truly engaged at work — despite the growing investment in culture-building initiatives. The issue isn't a lack of effort. It's a lack of authenticity.
              </p>

              <p>
                Modern workplaces often mistake fun for connection, and activity for alignment. But genuine connection — the kind that fosters trust, creativity, and resilience — doesn't happen under fluorescent lights or within 60 minutes of "escaping a room."
              </p>

              <p>
                It happens when people slow down. When they share space, silence, and stories. When they remember they are human first — colleagues second.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Authenticity: The Missing Ingredient</h2>

              <p>
                The most impactful professional experiences today aren't about thrill, but truth.
              </p>

              <p>
                Psychologist Brené Brown describes authenticity as "the daily practice of letting go of who we think we're supposed to be and embracing who we are." (<em>The Gifts of Imperfection</em>, Hazelden, 2010, p. 50). It's a quality that transforms individuals — and entire teams.
              </p>

              <p>
                At Mystic Forest, we believe leadership begins not with strategy, but with presence. Our retreats and workshops are built around moments that strip away noise and performance, allowing people to reconnect — with nature, with each other, and with themselves.
              </p>

              <p>
                Imagine a team conversation not over a PowerPoint, but around a campfire. Imagine learning active listening not from a slide, but through shared silence. Imagine cooperation built not on competition, but on trust born of real human contact.
              </p>

              <p>
                This isn't escapism. It's a return — to what collaboration truly means.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Science Behind Slowing Down</h2>

              <p>
                The value of mindfulness, rest, and presence in professional settings is now well-documented.
              </p>

              <p>
                A 2022 study published in <em>Frontiers in Psychology</em> (Good et al., 2022) found that short mindfulness-based group sessions significantly improved team empathy, communication, and problem-solving capacity. Similarly, research from Harvard Business School (Kallio, 2021) demonstrated that collective downtime — moments of reflection and non-task activity — strengthens creativity and cohesion in teams under chronic stress.
              </p>

              <p>
                In other words: stepping away from performance improves performance.
              </p>

              <p>
                In a forest setting, far from notifications and artificial deadlines, teams can engage in what psychologist Stephen Kaplan called "soft fascination" — the mental state where attention gently drifts without pressure, restoring focus and openness (Kaplan, <em>The Experience of Nature</em>, 1989, p. 192).
              </p>

              <p>
                This is where genuine insight happens — not in meetings, but in moments.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">From Workshops to Shared Rituals</h2>

              <p>
                What we offer at Mystic Forest isn't just another offsite. It's a shift in energy — from doing to being.
              </p>

              <p>
                Each program combines mindfulness, movement, and conversation, curated by therapists, facilitators, and leadership coaches. Workshops unfold organically — no rigid agendas, no buzzwords. Sometimes it's a forest walk. Sometimes a shared meal cooked over fire. Sometimes it's sitting in silence and realizing how rare that has become.
              </p>

              <p>
                These are the moments where masks fall, and leadership becomes human again.
              </p>

              <p>
                That's what we mean by authentic team growth.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why HR and Leaders Are Turning Toward Authentic Spaces</h2>

              <p>
                The post-pandemic workplace has changed. Teams are hybrid, attention is fragmented, and burnout has become a silent epidemic. According to the American Psychological Association's 2023 Work in America Survey, 77% of employees report work-related stress, and nearly half say it affects their relationships outside of work.
              </p>

              <p>
                In this landscape, leaders are no longer looking for "team-building." They are looking for healing, clarity, and connection.
              </p>

              <p>
                This is why companies like Patagonia, Airbnb, and Spotify have begun integrating restorative offsites focused on mindfulness, nature immersion, and self-awareness rather than high-adrenaline activities.
              </p>

              <p>
                At Mystic Forest, we echo that evolution — creating experiences that are both grounded and transformative.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Return to Meaningful Work</h2>

              <p>
                Ultimately, authentic leadership isn't taught — it's remembered. When people step into nature, away from noise, they begin to recognize what truly drives their collaboration: trust, empathy, presence.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "Work is love made visible."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Kahlil Gibran, <em>The Prophet</em>, Alfred A. Knopf, 1923, p. 22
                </footer>
              </blockquote>

              <p>
                And love — in a professional sense — means respect, attention, and shared humanity. It's what every great team is built upon.
              </p>

              <p>
                So perhaps the next "corporate offsite" doesn't need an escape room or a trophy. Maybe it needs something far more radical — a moment of stillness.
              </p>

              <p className="text-xl font-light italic text-accent mt-12">
                In the End
              </p>

              <p className="text-xl font-light">
                At Mystic Forest, we don't build teams — we restore human connection.
              </p>

              <p className="text-xl font-light">
                Offline. In nature. Together.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>Simon Sinek, <em>Leaders Eat Last</em> (Portfolio/Penguin, 2014), p. 112.</li>
                  <li>Brené Brown, <em>The Gifts of Imperfection</em> (Hazelden, 2010), p. 50.</li>
                  <li>Kaplan, Stephen. <em>The Experience of Nature: A Psychological Perspective</em> (Cambridge University Press, 1989), p. 192.</li>
                  <li>Good, D. J., et al. (2022). "Mindfulness and Team Performance: Enhancing Connection and Communication." <em>Frontiers in Psychology</em>, 13, 847292.</li>
                  <li>Kallio, J. (2021). "The Value of Collective Downtime: Creativity, Rest, and Team Insight." Harvard Business School Working Paper Series.</li>
                  <li>American Psychological Association. (2023). <em>Work in America Survey: Work and Well-being 2023</em>.</li>
                  <li>Gallup. (2023). <em>State of the Global Workplace Report</em>.</li>
                  <li>Kahlil Gibran, <em>The Prophet</em> (Alfred A. Knopf, 1923), p. 22.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default AuthenticExperiences;